import DynamicDialog from '@components/DynamicDialog';
import LoadingSpinner from '@components/LoadingSpinner';
import { useMemo, type CSSProperties, type ReactNode } from 'react';
import useDisclosure from '@/hooks/useDisclosure';
import { getCropImgSrc } from '@/helpers/functions';
import { cn } from '@helpers/cn';
import { omit } from 'lodash-es';
import useLoadImage, { type Image } from '@/hooks/useLoadImage';
import PlaceholderImage from '@components/PlaceholderImage';

type AspectRatio = `${number}/${number}`;

interface ImageWithPreviewProps {
  activatorProps?: {
    className?: string;
    [key: string]: unknown;
  };
  alt: string;
  fallbackImgSrc?: string;
  fitParent?: boolean;
  fitParentOptions?: {
    mode?: CSSProperties['objectFit'];
    position?: CSSProperties['objectPosition'];
  };
  imgSrc?: string;
  previewOptions?: {
    aspectRatio?: AspectRatio;
    width?: number;
  };
  small?: boolean;
  useNaturalImageSize?: boolean;
}

interface FullSizeImageProps {
  alt: ImageWithPreviewProps['alt'];
  aspectRatio?: AspectRatio;
  image: Image;
}

interface PreviewWrapperProps {
  children: ReactNode;
  className?: string;
  fitParent: ImageWithPreviewProps['fitParent'];
  previewImg: Image;
  previewOptions?: ImageWithPreviewProps['previewOptions'];
}

interface PreviewDefaults {
  aspectRatio: AspectRatio;
  height: number;
  objectFit: CSSProperties['objectFit'];
  objectPosition: CSSProperties['objectPosition'];
  width: number;
}

const PREVIEW_DEFAULTS = {
  aspectRatio: '16/9',
  height: 164,
  objectFit: 'contain',
  objectPosition: 'center',
  width: 290,
} as const satisfies PreviewDefaults;

const FullSizeImage = ({ alt, aspectRatio, image }: FullSizeImageProps) => {
  const { isError, isLoaded, image: img } = image ?? {};

  if (isError) return <PlaceholderImage />;

  if (!isLoaded) {
    return <LoadingSpinner height={PREVIEW_DEFAULTS.height} />;
  }

  return (
    <img
      alt={alt}
      src={img?.src}
      className={cn(
        'eye:z-1',
        aspectRatio &&
          'eye:relative eye:w-full eye:outline-4 eye:outline-black',
      )}
      style={aspectRatio ? { aspectRatio } : undefined}
    />
  );
};

const PreviewWrapper = ({
  children,
  fitParent,
  previewOptions,
  previewImg,
  className,
}: PreviewWrapperProps) => {
  const previewWidth = previewOptions?.width || PREVIEW_DEFAULTS.width;
  const previewWrapperWidth = usePreviewWrapperWidth(
    previewImg,
    previewWidth,
    fitParent,
  );

  return (
    <div
      aria-hidden
      style={{
        overflow: 'hidden',
        cursor: previewImg.isError ? 'default' : 'pointer',
        width: previewWrapperWidth,
        aspectRatio:
          previewOptions?.aspectRatio ?? PREVIEW_DEFAULTS.aspectRatio,
      }}
      className={className}
    >
      {children}
    </div>
  );
};

function ImageWithPreview({
  activatorProps = {},
  alt,
  fallbackImgSrc,
  fitParent = false,
  fitParentOptions,
  imgSrc,
  previewOptions,
  small = false,
  useNaturalImageSize = false,
}: ImageWithPreviewProps) {
  const { isOpen: isDialogOpen, toggle: toggleDialog } = useDisclosure(false);
  const previewImgSrc = getCropImgSrc(imgSrc, previewOptions);
  const previewImg = useLoadImage(previewImgSrc, fallbackImgSrc);
  const fullSizeImg = useLoadImage(imgSrc, fallbackImgSrc, isDialogOpen);

  if (previewImg.isError) {
    return (
      <PreviewWrapper
        previewImg={previewImg}
        previewOptions={previewOptions}
        fitParent={fitParent}
        className='eye:flex eye:justify-center eye:items-center'
      >
        <PlaceholderImage />
      </PreviewWrapper>
    );
  }

  if (!previewImg.image) {
    return <LoadingSpinner height={PREVIEW_DEFAULTS.height} />;
  }

  return (
    <DynamicDialog
      activatorProps={{
        'aria-label': alt,
        className: cn(
          'eye:overflow-hidden eye:w-full eye:border! eye:border-grey-20!',
          !fitParent && `eye:min-h-[${PREVIEW_DEFAULTS.height}px]`,
          activatorProps?.className,
        ),
        // TODO: eye:p-0 does not work 🫤
        style: { padding: 0 },
        ...omit(activatorProps, 'className'),
      }}
      activatorText={
        <PreviewWrapper
          previewImg={previewImg}
          previewOptions={previewOptions}
          fitParent={fitParent}
        >
          <img
            alt={alt}
            loading='eager'
            src={previewImg.image.src}
            className={cn(
              'eye:absolute eye:top-0 eye:left-0 eye:w-full eye:h-auto',
              fitParent && 'eye:h-full',
            )}
            style={{
              ...(fitParent && {
                objectFit: fitParentOptions?.mode || PREVIEW_DEFAULTS.objectFit,
                objectPosition:
                  fitParentOptions?.position || PREVIEW_DEFAULTS.objectPosition,
              }),
            }}
          />
        </PreviewWrapper>
      }
      fullWidth
      alwaysFullscreen={!small}
      aria-hidden
      hideActions
      onOpen={() => {
        toggleDialog();
        return true;
      }}
      onCancel={() => {
        toggleDialog();
        return true;
      }}
      title={alt}
    >
      {useNaturalImageSize ? (
        <div className='eye:text-center'>
          <FullSizeImage image={fullSizeImg} alt={alt} />
        </div>
      ) : (
        <FullSizeImage
          image={fullSizeImg}
          alt={alt}
          {...(fullSizeImg.image && {
            aspectRatio: `${fullSizeImg.image.naturalWidth}/${fullSizeImg.image.naturalHeight}`,
          })}
        />
      )}
    </DynamicDialog>
  );
}

function usePreviewWrapperWidth(
  previewImg: Image,
  width: number,
  fitParent?: boolean,
) {
  const naturalSize = useMemo(() => {
    if (!previewImg.image) return { width: 0, height: 0 };

    return {
      width: previewImg.image.naturalWidth,
      height: previewImg.image.naturalHeight,
    };
  }, [previewImg.image]);

  const wrapperWidth = useMemo(() => {
    if (fitParent) return '100%';

    return Math.min(naturalSize.width, width);
  }, [fitParent, width, naturalSize.width]);

  return wrapperWidth;
}

export default ImageWithPreview;
